
import React from 'react';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip } from 'recharts';
import { AiAnalysis, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { Bot, ThumbsUp, Activity, CheckCircle, ShieldAlert } from 'lucide-react';

interface AiAnalysisCardProps {
  data: AiAnalysis;
  clientScore: number;
  language: Language;
}

export const AiAnalysisCard: React.FC<AiAnalysisCardProps> = ({ data, clientScore, language }) => {
  const t = TRANSLATIONS[language];

  const chartData = [
    { subject: t.authenticity, A: data.authenticity, fullMark: 100 },
    { subject: t.sentiment, A: data.sentiment, fullMark: 100 },
    { subject: t.commercialValue, A: data.commercialValue, fullMark: 100 },
    { subject: t.brandSafety, A: data.brandSafety, fullMark: 100 },
    { subject: t.consistency, A: data.consistency, fullMark: 100 },
  ];

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden p-6">
      <div className="flex items-center gap-2 mb-6">
        <Bot className="text-indigo-400 w-6 h-6" />
        <h3 className="text-lg font-bold text-white">{t.aiAnalysis}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Radar Chart */}
        <div className="h-64 relative">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={chartData}>
              <PolarGrid stroke="#334155" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
              <Radar
                name="AI Score"
                dataKey="A"
                stroke="#818cf8"
                strokeWidth={2}
                fill="#6366f1"
                fillOpacity={0.4}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
              />
            </RadarChart>
          </ResponsiveContainer>
          
          {/* Scores Overlay */}
          <div className="absolute top-0 right-0 flex flex-col gap-2">
             <div className="bg-slate-900/80 p-2 rounded-lg border border-indigo-500/30">
                <span className="text-xs text-indigo-300 block">{t.aiScore}</span>
                <span className="text-xl font-bold text-white">{data.overallScore}</span>
             </div>
             <div className="bg-slate-900/80 p-2 rounded-lg border border-emerald-500/30">
                <span className="text-xs text-emerald-300 block">{t.clientScore}</span>
                <span className="text-xl font-bold text-white">{clientScore}</span>
             </div>
          </div>
        </div>

        {/* Text Analysis */}
        <div className="space-y-4">
          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
             <h4 className="text-sm font-semibold text-slate-300 mb-2">{t.aiSummary}</h4>
             <p className="text-sm text-slate-400 leading-relaxed">{data.summary}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h5 className="text-xs font-semibold text-emerald-400 mb-2 flex items-center gap-1">
                <CheckCircle size={12} /> {t.strengths}
              </h5>
              <ul className="space-y-1">
                {data.strengths.map((item, idx) => (
                  <li key={idx} className="text-xs text-slate-300 flex items-start gap-1">
                    <span className="text-emerald-500">•</span> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="text-xs font-semibold text-rose-400 mb-2 flex items-center gap-1">
                <ShieldAlert size={12} /> {t.weaknesses}
              </h5>
              <ul className="space-y-1">
                {data.weaknesses.map((item, idx) => (
                  <li key={idx} className="text-xs text-slate-300 flex items-start gap-1">
                    <span className="text-rose-500">•</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
